import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Database, Plug, Save, Loader2, CheckCircle2, XCircle, ShieldCheck, RefreshCw, Terminal } from 'lucide-react';
import api, { formatApiError } from '@/lib/api';

const EMPTY = {
  host: '', port: 3306, user: '', password: '', db: 'radius',
  ssh_enabled: false, ssh_host: '', ssh_port: 22, ssh_user: '', ssh_password: '', sudo_password: '',
  reload_command: 'sudo systemctl reload freeradius',
};

export default function RadiusConnection({ onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/radius/config');
      setCfg(data);
      setForm({
        host: data.host || '', port: data.port || 3306, user: data.user || '', password: '', db: data.db || 'radius',
        ssh_enabled: !!data.ssh_enabled, ssh_host: data.ssh_host || '', ssh_port: data.ssh_port || 22,
        ssh_user: data.ssh_user || '', ssh_password: '', sudo_password: '',
        reload_command: data.reload_command || 'sudo systemctl reload freeradius',
      });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const doReload = async () => {
    setReloading(true);
    try {
      const { data } = await api.post('/radius/reload');
      if (data.ok) toast.success('FreeRADIUS berhasil di-reload');
      else toast.warning('FreeRADIUS belum di-reload', { description: data.message, duration: 9000 });
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setReloading(false); }
  };

  const doTest = async () => {
    setTesting(true); setResult(null);
    try {
      const { data } = await api.post('/radius/config/test', { ...form, port: Number(form.port) });
      setResult(data);
      if (data.ok) toast.success(`Terhubung · ${data.version}${data.tables_ok ? '' : ' (tabel RADIUS tidak lengkap)'}`);
      else toast.error(data.error || 'Gagal terhubung');
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setTesting(false); }
  };

  const doSave = async () => {
    if (!form.host || !form.user || !form.db) return toast.error('Host, Username, dan Database wajib diisi');
    setSaving(true); setResult(null);
    try {
      const { data } = await api.put('/radius/config', { ...form, port: Number(form.port) });
      setResult(data.test);
      if (data.test?.ok) toast.success('Koneksi disimpan & terhubung ke MariaDB RADIUS');
      else toast.warning('Koneksi disimpan, namun test gagal — periksa detail di bawah');
      await load();
      onSaved && onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="radius-connection">
      <Card className="border-border"><CardContent className="p-4 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="p-2 rounded-lg bg-sky-500/10 border border-sky-500/30"><Database className="w-4 h-4 text-sky-500" /></span>
            <div>
              <div className="text-lg font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>Koneksi Database RADIUS</div>
              <p className="text-xs text-muted-foreground">Arahkan Portal ke MariaDB <span className="font-mono">radius</span> yang dipakai FreeRADIUS existing Anda. Portal hanya membaca/menulis schema RADIUS; FreeRADIUS tetap authentication server.</p>
            </div>
          </div>
          {cfg && (cfg.configured
            ? <Badge className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 border border-emerald-500/30">Tersimpan</Badge>
            : <Badge className="bg-amber-500/15 text-amber-600 dark:text-amber-300 border border-amber-500/30">Default (env dev)</Badge>)}
        </div>

        {loading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Memuat…</div>
        ) : (
          <div className="space-y-3 max-w-2xl">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2"><Label className="text-xs">Host / IP MariaDB</Label><div className="mt-1"><Input value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} placeholder="mis. 10.10.0.10 atau IP VPN" data-testid="radius-conn-host" /></div></div>
              <div><Label className="text-xs">Port</Label><div className="mt-1"><Input type="number" value={form.port} onChange={(e) => setForm({ ...form, port: e.target.value })} placeholder="3306" data-testid="radius-conn-port" /></div></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label className="text-xs">Username</Label><div className="mt-1"><Input value={form.user} onChange={(e) => setForm({ ...form, user: e.target.value })} placeholder="radius" data-testid="radius-conn-user" /></div></div>
              <div><Label className="text-xs">Password {cfg?.has_password ? '(kosongkan = tetap)' : ''}</Label><div className="mt-1"><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={cfg?.has_password ? '••••••••' : ''} data-testid="radius-conn-password" /></div></div>
            </div>
            <div><Label className="text-xs">Database Name</Label><div className="mt-1"><Input value={form.db} onChange={(e) => setForm({ ...form, db: e.target.value })} placeholder="radius" data-testid="radius-conn-db" /></div></div>

            <div className="flex items-center gap-2 pt-1">
              <Button variant="outline" onClick={doTest} disabled={testing || !form.host} data-testid="radius-conn-test">{testing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Plug className="w-4 h-4 mr-1.5" />} Test Connection</Button>
              <Button onClick={doSave} disabled={saving} data-testid="radius-conn-save">{saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />} Simpan</Button>
            </div>

            {result && (
              <div className={`rounded-md border p-3 text-sm ${result.ok ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-rose-500/40 bg-rose-500/5'}`} data-testid="radius-conn-result">
                {result.ok ? (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-300 font-medium"><CheckCircle2 className="w-4 h-4" /> Terhubung ke MariaDB</div>
                    <div className="text-xs text-muted-foreground">Versi: <span className="font-mono">{result.version}</span></div>
                    <div className="text-xs flex items-center gap-1.5">{result.tables_ok ? <><ShieldCheck className="w-3.5 h-3.5 text-emerald-500" /> Semua tabel RADIUS tersedia</> : <><XCircle className="w-3.5 h-3.5 text-amber-500" /> Tabel belum lengkap: <span className="font-mono">{(result.missing_tables || []).join(', ')}</span></>}</div>
                  </div>
                ) : (
                  <div className="flex items-start gap-2 text-rose-600 dark:text-rose-300"><XCircle className="w-4 h-4 mt-0.5" /><div><div className="font-medium">Gagal terhubung</div><div className="text-xs text-muted-foreground break-all">{result.error}</div>{result.hint && <div className="text-xs mt-1.5 text-amber-600 dark:text-amber-300 break-words">💡 {result.hint}</div>}</div></div>
                )}
              </div>
            )}

            <div className="text-[11px] text-muted-foreground border-t border-border pt-3">
              <p className="font-medium text-foreground/80 mb-1">Catatan koneksi:</p>
              <ul className="list-disc pl-4 space-y-0.5">
                <li>Portal di cloud tidak bisa menjangkau IP LAN privat (127.0.0.1 / 10.x / 192.168.x) di server Anda — gunakan IP publik ter-restrict atau VPN, atau deploy Portal di server yang sama.</li>
                <li>MariaDB harus mengizinkan koneksi dari host Portal (bind-address + GRANT user).</li>
                <li>Password & secret tidak pernah ditampilkan kembali; disimpan terenkripsi.</li>
              </ul>
            </div>
          </div>
        )}
      </CardContent></Card>

      <Card className="border-border"><CardContent className="p-4 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="p-2 rounded-lg bg-violet-500/10 border border-violet-500/30"><RefreshCw className="w-4 h-4 text-violet-500" /></span>
            <div>
              <div className="text-lg font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>Auto-reload FreeRADIUS (SSH)</div>
              <p className="text-xs text-muted-foreground">FreeRADIUS hanya membaca klien (tabel <span className="font-mono">nas</span>) saat reload. Aktifkan agar Portal otomatis reload FreeRADIUS via SSH setiap NAS ditambah/diubah/dihapus — router baru langsung bisa autentikasi.</p>
            </div>
          </div>
          {cfg && (cfg.ssh_enabled
            ? <Badge className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 border border-emerald-500/30">Aktif</Badge>
            : <Badge className="bg-amber-500/15 text-amber-600 dark:text-amber-300 border border-amber-500/30">Nonaktif</Badge>)}
        </div>

        {!loading && (
          <div className="space-y-3 max-w-2xl">
            <div className="flex items-center justify-between rounded-md border border-border p-3">
              <div><div className="text-sm font-medium">Aktifkan auto-reload</div><div className="text-xs text-muted-foreground">Kirim perintah reload ke server FreeRADIUS via SSH.</div></div>
              <Switch checked={form.ssh_enabled} onCheckedChange={(v) => setForm({ ...form, ssh_enabled: v })} data-testid="radius-ssh-enabled" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2"><Label className="text-xs">SSH Host (server FreeRADIUS)</Label><div className="mt-1"><Input value={form.ssh_host} onChange={(e) => setForm({ ...form, ssh_host: e.target.value })} placeholder="kosong = pakai Host MariaDB di atas" data-testid="radius-ssh-host" /></div></div>
              <div><Label className="text-xs">SSH Port</Label><div className="mt-1"><Input type="number" value={form.ssh_port} onChange={(e) => setForm({ ...form, ssh_port: e.target.value })} placeholder="22" data-testid="radius-ssh-port" /></div></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label className="text-xs">SSH Username</Label><div className="mt-1"><Input value={form.ssh_user} onChange={(e) => setForm({ ...form, ssh_user: e.target.value })} placeholder="mis. noc / root" data-testid="radius-ssh-user" /></div></div>
              <div><Label className="text-xs">SSH Password {cfg?.ssh_has_password ? '(kosongkan = tetap)' : ''}</Label><div className="mt-1"><Input type="password" value={form.ssh_password} onChange={(e) => setForm({ ...form, ssh_password: e.target.value })} placeholder={cfg?.ssh_has_password ? '••••••••' : ''} data-testid="radius-ssh-password" /></div></div>
            </div>
            <div><Label className="text-xs">Sudo Password {cfg?.sudo_has_password ? '(kosongkan = tetap)' : '(bila sudo minta password)'}</Label><div className="mt-1"><Input type="password" value={form.sudo_password} onChange={(e) => setForm({ ...form, sudo_password: e.target.value })} placeholder={cfg?.sudo_has_password ? '••••••••' : 'kosongkan bila NOPASSWD sudo'} data-testid="radius-sudo-password" /></div><p className="text-[11px] text-muted-foreground mt-1">Diisi bila user SSH butuh password untuk <span className="font-mono">sudo</span>. Password dikirim aman via stdin (tidak muncul di daftar proses).</p></div>
            <div><Label className="text-xs flex items-center gap-1.5"><Terminal className="w-3.5 h-3.5" /> Perintah Reload</Label><div className="mt-1"><Input value={form.reload_command} onChange={(e) => setForm({ ...form, reload_command: e.target.value })} placeholder="sudo systemctl reload freeradius" className="font-mono text-xs" data-testid="radius-ssh-cmd" /></div><p className="text-[11px] text-muted-foreground mt-1">Contoh alternatif: <span className="font-mono">sudo systemctl restart freeradius</span>. Pastikan user SSH boleh menjalankannya tanpa prompt (NOPASSWD sudo).</p></div>

            <div className="flex items-center gap-2 pt-1">
              <Button onClick={doSave} disabled={saving} data-testid="radius-ssh-save">{saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />} Simpan</Button>
              <Button variant="outline" onClick={doReload} disabled={reloading} data-testid="radius-ssh-reload">{reloading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />} Reload FreeRADIUS Sekarang</Button>
            </div>
            {cfg?.last_reload_at && (
              <div className="text-[11px] text-muted-foreground">Reload terakhir: {new Date(cfg.last_reload_at).toLocaleString('id-ID')} — {cfg.last_reload_ok ? <span className="text-emerald-500">berhasil</span> : <span className="text-rose-500">gagal</span>}{cfg.last_reload_message ? ` (${cfg.last_reload_message})` : ''}</div>
            )}
          </div>
        )}
      </CardContent></Card>
    </div>
  );
}
